import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Website} from "./website";
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Action} from "../../action-manager/action/action";
import {User} from "../../user/user";
import * as fromStore from '../../store/index';
import {Store} from '@ngrx/store';
import {filter, first, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {CookieService} from "ngx-cookie";
import {UserSubscription, UserSubscriptionIds} from '../../user/user-subscription';
import {RouteData} from "../../shared/services/route-data/route-data";
import {RouteDataService} from "../../shared/services/route-data/route-data.service";
import {TypeUtils} from "../../shared/utils/type-utils";
import {Product} from "./product";
import {ToggleWebsiteSidebar} from "../../store";
import {Subject} from "rxjs";
import {combineLatest} from "rxjs";
import {Config} from "../../config/config";
import {ActivityAction} from "../../activities/activity";

@Component({
    selector: 'db-website',
    templateUrl: './website.component.html',
    styleUrls: ['./website.component.scss'],
    standalone: false
})
export class WebsiteComponent implements OnInit, OnDestroy {

  public isPHPUpToDate: boolean = true;
  public _user: User;
  public userSubscription: UserSubscription;
  public website: Website;
  public blockerAction: Action;
  public isMobile: boolean;
  public routeData: RouteData;
  public optimizerPlugin: Product;
  public userSubscriptionIds = UserSubscriptionIds;
  public hideOptimizerError: boolean = false;
  public sidebarIsToggled = false;
  private _destroyStream$ = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _cookieService: CookieService,
    private _routeDataService: RouteDataService,
  ) {}

  ngOnInit() {
    this.website = this._route.snapshot.data['website'];
    this.website && this.website.cloudflareEnabled && this._store.dispatch(new fromStore.LoadCloudflareData(this.website?.id));
    this._user = this._route.snapshot.data['user'];
    this.userSubscription = this._route.snapshot.data['userSubscription'];
    this.routeData = this._routeDataService.getCurrentRouteData();
    if (typeof this._cookieService.get('db-original-user-id') === 'undefined') {
      this._cookieService.put('db-original-user-id', this._user.id, {path: '/', domain: '10web.io'});
    }
    this.isMobile = this._user && window.innerWidth <= 1024;
    this._getWebsite();
    this._getOptimizerPlugin();
    this._getPHPVersion();
    this._pluginsUpdate();
  }

  public toggleWebsiteSidebar(): void {
    this._store.dispatch(new ToggleWebsiteSidebar);
    this._store.select(fromStore.getWebsiteSidebarToggled)
      .subscribe(isToggled => {
        this.sidebarIsToggled = isToggled;
      });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _getWebsite(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        switchMap(() => this._store.select(fromStore.getWebsitesLoaded)),
        filter(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getWebsite(this._route.snapshot.data['website'] && this._route.snapshot.data['website'].id))),
        takeUntil(this._destroyStream$),
      )
      .subscribe((website: Website) => {
        if (website) {
          this.routeData = this._routeDataService.getCurrentRouteData();
          this.website = website;
          this.hideOptimizerError = !(this.website.score && this.website.score.currentScore && this.website.score.currentScore.desktopScore);
        }
      });
  }

  private _getOptimizerPlugin(): void {
    this._store.select(fromStore.getWebsiteOptimizerPlugin(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((optimizerPlugin): void => {
        if (optimizerPlugin && !TypeUtils.isEmpty(optimizerPlugin)) {
          this.optimizerPlugin = optimizerPlugin;
        }
      });
  }

  private _getPHPVersion(): void {
    this._store.select(fromStore.getHostingSettings(this.website?.isNetwork && !this.website?.parentId
    && this.website?.children && this.website?.children.length ? this.website?.children[0] : this.website?.id))
      .pipe(
        filter(response => !!response),
        takeUntil(this._destroyStream$)
      )
      .subscribe((settings: any) => {
        this.isPHPUpToDate = settings && settings.php_version && settings.php_version.substring(0, 3) >= "7.4";
      });
  }

  private _pluginsUpdate(): void {
    if (!this.website?.isActive || this.website?.isLocalhost || this.website?.connectedFrom === 'speed_optimizer') return;

    const websiteId = this.website?.isNetwork && !this.website?.parentId ? this.website?.children[0] : this.website?.id;

    this._store.select(fromStore.getWebsitePluginsLoaded(websiteId))
      .pipe(
        first(loaded => loaded, null),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsiteOptimizerPlugin(websiteId)),
/*
          this._store.select(fromStore.getWebsiteBuilderPlugin(websiteId)),
*/
          this._store.select(fromStore.getWebsiteImageOptimizerPlugin(websiteId)),
          this._store.select(fromStore.getWebsiteBackupPlugin(websiteId)),
          this._store.select(fromStore.getWebsiteSeoPlugin(websiteId)),
          this._store.select(fromStore.getWebsiteSecurityPlugin(websiteId)),
        )),
        first()
      )
      .subscribe((products) => {
        const existingProducts = products.filter(product => !!product);
        for (const product of existingProducts) {
          ((product.slug === Config.optimizerPluginSlug && !this.website.domain_hash) || product.slug !== Config.optimizerPluginSlug) && this._pluginUpdate(product, this.website);
        }
      });
  }

  private _pluginUpdate(product, website): void {
    if (product && product.updateAvailable) {
      this._store.dispatch(new fromStore.InitWebsiteProductAction({
        website: website,
        products: [product],
        action: ActivityAction.UPDATE,
        network: 1
      }));
    }
  }
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isMobile = window.innerWidth <= 1024;
  }
}
